/**
 * PDF Embed section — lightweight, self-hosted PDF.js viewer.
 * Renders a PDF onto a <canvas> with Prev/Next + zoom controls styled
 * by the theme's own CSS (see sections/pdf-embed.liquid), instead of
 * loading Mozilla's hosted viewer UI.
 */
class PdfEmbedViewer extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('[data-pdf-canvas]');
    this.prevBtn = this.querySelector('[data-pdf-prev]');
    this.nextBtn = this.querySelector('[data-pdf-next]');
    this.pageIndicator = this.querySelector('[data-pdf-page-indicator]');
    this.zoomInBtn = this.querySelector('[data-pdf-zoom-in]');
    this.zoomOutBtn = this.querySelector('[data-pdf-zoom-out]');
    this.loadingEl = this.querySelector('[data-pdf-loading]');
    this.errorEl = this.querySelector('[data-pdf-error]');

    this.pdfUrl = this.dataset.pdfUrl;
    this.pdfjsUrl = this.dataset.pdfjsUrl;
    this.workerUrl = this.dataset.workerUrl;
    this.pageNum = 1;
    this.scale = 1.0;
    this.minScale = 0.5;
    this.maxScale = 3;
    this.pdfDoc = null;

    if (!this.pdfUrl || !this.pdfjsUrl || !this.workerUrl) return;

    this.init();
  }

  async init() {
    try {
      const pdfjsLib = await import(this.pdfjsUrl);
      pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerUrl;
      this.pdfDoc = await pdfjsLib.getDocument(this.pdfUrl).promise;
      this.numPages = this.pdfDoc.numPages;
      if (this.loadingEl) this.loadingEl.hidden = true;
      this.bindControls();
      await this.renderPage(this.pageNum);
    } catch (error) {
      console.error('[pdf-embed-viewer] failed to load PDF', error);
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.errorEl) this.errorEl.hidden = false;
    }
  }

  bindControls() {
    this.prevBtn?.addEventListener('click', () => {
      if (this.pageNum <= 1) return;
      this.pageNum -= 1;
      this.renderPage(this.pageNum);
    });
    this.nextBtn?.addEventListener('click', () => {
      if (this.pageNum >= this.numPages) return;
      this.pageNum += 1;
      this.renderPage(this.pageNum);
    });
    this.zoomInBtn?.addEventListener('click', () => {
      this.scale = Math.min(this.scale + 0.25, this.maxScale);
      this.renderPage(this.pageNum);
    });
    this.zoomOutBtn?.addEventListener('click', () => {
      this.scale = Math.max(this.scale - 0.25, this.minScale);
      this.renderPage(this.pageNum);
    });
  }

  async renderPage(num) {
    if (!this.pdfDoc || !this.canvas) return;
    const page = await this.pdfDoc.getPage(num);
    const pixelRatio = window.devicePixelRatio || 1;
    const viewport = page.getViewport({ scale: this.scale * pixelRatio });
    const ctx = this.canvas.getContext('2d');

    this.canvas.width = viewport.width;
    this.canvas.height = viewport.height;
    // Don't set an inline pixel width/height here: it fights the responsive
        // CSS (.pdf-embed__canvas { max-width: 100%; height: auto; }) and causes
        // the canvas to render squashed on narrow viewports. canvas.width/height
        // above already set the intrinsic pixel size (for crisp devicePixelRatio
        // rendering); leaving style.width/height unset lets the canvas scale like
        // a normal replaced element and keep its aspect ratio.
    
    await page.render({ canvasContext: ctx, viewport }).promise;

    if (this.pageIndicator) this.pageIndicator.textContent = `${num} / ${this.numPages}`;
    if (this.prevBtn) this.prevBtn.disabled = num <= 1;
    if (this.nextBtn) this.nextBtn.disabled = num >= this.numPages;
    if (this.zoomOutBtn) this.zoomOutBtn.disabled = this.scale <= this.minScale;
    if (this.zoomInBtn) this.zoomInBtn.disabled = this.scale >= this.maxScale;
  }
}

if (!customElements.get('pdf-embed-viewer')) {
  customElements.define('pdf-embed-viewer', PdfEmbedViewer);
}
